/* eslint-disable import/no-extraneous-dependencies */
import 'semantic-ui-css/semantic.min.css';
import './_App.scss';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { ModalController } from 'controls/modals';
import { useNotificationScript } from 'controls/hooks/useNotificationScript';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';
import { appendAdobeAnalyticsScript } from 'libs/adobeAnalytics';
import { RouteErrorBoundary } from 'components/common/routeErrorBoundary';
import { rootRedirectLoader } from 'loaders/rootLoader';
import { ChakraProvider, defaultSystem } from '@chakra-ui/react';
import { useAuth } from 'context/AuthContext';
import { isEmbedRoute } from 'libs/embed';
import ReactGA from 'react-ga4';

const RCWPage = lazy(() => import('components/pages/RCWPage/RCWPage').then((m) => ({ default: m.RCWPage })));
const ThankYouPage = lazy(() => import('components/pages/RCWPage/ThankYouPage').then((m) => ({ default: m.ThankYouPage })));
const ComparePage = lazy(() => import('components/pages/ComparePage/ComparePage').then((m) => ({ default: m.ComparePage })));

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path='/thank-you'
        element={<Suspense fallback={<LoaderSpinner />}><ThankYouPage /></Suspense>}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path='/compare'
        element={<Suspense fallback={<LoaderSpinner />}><ComparePage /></Suspense>}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path='/callback'
        element={null}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path='/'
        element={<Suspense fallback={<LoaderSpinner />}><RCWPage /></Suspense>}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path='*'
        loader={rootRedirectLoader}
        element={null}
      />
    </>,
  ),
  {
    basename: process.env.REACT_APP_BASE_PATH,
  },
);

export function App() {
  const modals = useSelector((state) => state.modals);
  const notificationScript = useNotificationScript();
  const dispatch = useDispatch();
  const { user, isLoading } = useAuth();

  // The RCM-embedded route runs without a cookie banner, so it must stay free of
  // anything that sets one. See libs/embed.js.
  const isEmbedded = isEmbedRoute();

  useEffect(() => {
    if (isEmbedded) return;
    appendAdobeAnalyticsScript();
  }, [isEmbedded]);

  useEffect(() => {
    if (isEmbedded) return;
    ReactGA.initialize('G-T4G98Z525E');
  }, [isEmbedded]);

  useEffect(() => {
    if (user) {
      fetch(`${process.env.REACT_APP_AUTHORITY}me`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${user.id_token}` },
      })
        .then((res) => res.json())
        .then((profileData) => {
          const cultureCode = profileData?.user?.cultureCode || 'en';
          const userObj = {
            email: user.profile.email,
            name: user.profile.name,
            token: user.access_token,
            idToken: user.id_token,
            cultureCode,
          };
          dispatch({ type: 'SET_USER', user: userObj });
        })
        .catch(() => {
          const userObj = {
            email: user.profile.email,
            name: user.profile.name,
            token: user.access_token,
            idToken: user.id_token,
            cultureCode: 'en',
          };
          dispatch({ type: 'SET_USER', user: userObj });
        });
    } else if (!isLoading) {
      dispatch({ type: 'SET_USER', user: { guest: true } });
    }
  }, [user, isLoading, dispatch]);

  return (
    <ChakraProvider value={defaultSystem}>
      <div className='App'>
        { notificationScript
        && (
        <Helmet>
          <script>
            {notificationScript}
          </script>
        </Helmet>
        )}
        <link
          href='https://unpkg.com/tachyons@4/css/tachyons.min.css'
        />
        <link rel='preconnect' href='https://fonts.gstatic.com' />
        <link href='https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap' rel='stylesheet' />
        <link href='https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap' rel='stylesheet' />
        <RouterProvider router={router} />
        <ModalController modals={modals} />
      </div>
    </ChakraProvider>
  );
}
